import React from 'react'
import ApiClient from 'api-client/ApiClient'
import ExternalLink from 'core/components/ExternalLink'
import Text from 'core/elements/Text'
import { kubeOsImageDoc } from 'app/docLinks'
import DataKeys from 'k8s/DataKeys'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'

/**
 * @summary Custom hook to return matching images for k8s version
 * @param k8sVersion
 * @param setError
 * @param images
 * @returns matching images for provided k8s version OR empty array
 */
export const METALLB_VERSION = '0.14.9'

const MAX_POLLING_ATTEMPTS = 60 // 5 minutes timeout (60 × 5 seconds)

/**
 * @summary Polls KaapiClusters until the TopologyReconciled condition is True.
 * Resolves when reconciled; throws if maxAttempts is exceeded.
 */
export const waitForTopologyReconciliation = async ({
  clusterName,
  maxAttempts = MAX_POLLING_ATTEMPTS,
}: {
  clusterName: string
  maxAttempts?: number
}): Promise<void> => {
  const { kubernetes } = ApiClient.getInstance()
  let attempts = 0

  while (attempts < maxAttempts) {
    attempts++
    const clusters = await kubernetes.getAllCustomResources(DataKeys.KaapiClusters)
    const cluster = clusters?.find((c) => c?.metadata?.name === clusterName)

    if (
      cluster?.status?.conditions?.some(
        (condition) => condition.type === 'TopologyReconciled' && condition.status === 'True',
      )
    ) {
      return
    }

    await waitSeconds(5)
  }

  throw new Error('Node groups are taking too long to get deployed')
}

export const getK8sImages = ({ k8sVersion, setError, images, gpuAddons = false }) => {
  // If k8sVersion not found return null
  if (!k8sVersion) {
    setError({
      title: 'Error creating cluster',
      message: 'Kubernetes version not available',
    })
    return []
  }

  // Only take major and minor version Eg. If 1.30.11 take 1.30
  // Cluster class adds 'v' as preffix, remove v as well.
  const formattedk8sVersion = k8sVersion
    .split('.')
    .slice(0, 2)
    .join('.')
    .replace(/^v/, '')

  // Find all images for provided k8s version
  let matchingK8sImages = images?.filter(
    (image) => image?.status === 'active' && image?.properties?.k8s_version === formattedk8sVersion,
  )

  // If GPU is enabled find image with GPU support
  if (gpuAddons) {
    matchingK8sImages = matchingK8sImages?.filter((image) => image?.properties?.gpu === 'true')
  }

  // If matching image not found return null
  if (!matchingK8sImages || matchingK8sImages.length === 0) {
    setError({
      title: 'No images available',
      message: (
        <>
          <Text variant="body2">
            {gpuAddons ? 'No GPU supported images available' : 'No images available'} for selected
            Kubernetes version.
          </Text>
          <Text variant="caption1">
            <ExternalLink url={kubeOsImageDoc}>
              <b>Learn More Here</b>
            </ExternalLink>
          </Text>
        </>
      ),
    })
    return []
  }

  return matchingK8sImages?.map((image) => image?.name)
}

export const generateImageOptions = (images: string[]) => {
  return images?.map((image) => ({ label: image, value: image }))
}
